<template>
  <div class="about">
    <SiteHeader/>
    <main class="splash-page-container">
      <div id="amplify-audience">
        <div class="left-background"></div>
        <div class="section-content">
          <div class="left-section">
            <div class="info-header-a robotoB">A Psychographic Interests Platform</div>
            <div class="info-body-copy-a">
              <p>In today’s fragmented world, psychographic profiling is more important to marketers than ever. Yet the
                art of figuring out how to effectively reach and convert audiences remains a daunting task. Ranker
                Insights brings precision and depth to what has typically been the very “fuzzy” exercise of
                psychographic profiling.</p>
              <p>Each month over 50 million people visit <a href="http://www.ranker.com/" target="_blank">Ranker</a> to
                cast their votes on thousands of online polls about Films, Celebrities, TV, Music, Sports, and more,
                providing a treasure trove of proprietary self-reported preference data across 1.1 million interests.
              </p>
              <p>Powered by Ranker’s unprecedented data collection engine, the Ranker Insights platform was developed to
                provide data-driven audience insights, both at scale and in precise context. <a
                    href="mailto:insights@ranker.com?Subject=Ranker%20Insights%20Custom%20API" target="_blank">Contact
                  us</a> to learn more about how we can work with you on a custom API basis.</p>
            </div>
            <div id="insert-paragraph"></div>
          </div>
          <div class="right-section">
            <NodeCycler/>
          </div>
        </div>
        <div class="right-background"></div>
      </div>
      <div id="examples">
        <div class="left-background"></div>
        <div class="section-content">
          <div class="info-header-a">Turn Challenging Campaigns Into Laser-Targeted Wins</div>
          <div class="info-body-copy-a">
            <p>Too often marketers are forced to rely upon generic surface-level data when making pivotal decisions. By
              collecting and connecting audience opinions across 1.1 million interests, Ranker Insights unmasks
              not-so-obvious correlations between the people, places, and things that fans are most passionate about.
            </p>
            <p>Marketers are using Ranker Insights intel to inform the creative process, laser-target the right
              audiences (and avoid the wrong ones), buy media more efficiently, and make data-driven strategic
              decisions.</p>
          </div>
          <div id="node-examples">
            <a :href="`${getInsightsUrl()}/?adjectives=any&amp;interstitial=1&amp;id=63400045`"
               class="view-report-link">
              <div id="node-example-1" class="node-example">
                <div class="node-example-button"></div>
              </div>
            </a>
            <a :href="`${getInsightsUrl()}/?adjectives=any&amp;interstitial=1&amp;id=1337602`"
               class="view-report-link">
              <div id="node-example-2" class="node-example">
                <div class="node-example-button"></div>
              </div>
            </a>
            <a :href="`${getInsightsUrl()}/?adjectives=any&amp;interstitial=1&amp;id=2087355`"
               class="view-report-link">
              <div id="node-example-3" class="node-example">
                <div class="node-example-button"></div>
              </div>
            </a>
            <a :href="`${getInsightsUrl()}/?adjectives=any&amp;interstitial=1&amp;id=74085924`"
               class="view-report-link">
              <div id="node-example-4" class="node-example">
                <div class="node-example-button"></div>
              </div>
            </a>
          </div>
          <div id="example-bottom" class="clearfix"></div>
        </div>
        <div class="right-background"></div>
      </div>
      <div id="map">
        <div class="left-background"></div>
        <div class="section-content"></div>
        <div class="right-background"></div>
      </div>
      <div id="bullets-header">
        <div class="left-background"></div>
        <div class="section-content">
          <div class="info-header-a">How Marketers Are Using Ranker Insights</div>
        </div>
        <div class="right-background"></div>
      </div>
      <div id="bullets">
        <div class="left-background"></div>
        <div class="section-content">
          <div class="bullet-header">Buy Media More Efficiently</div>
          <div class="bullet-text">Ranker Insights gives, for example, media buyers in TV advertising clear data to go
            beyond demographics and set-top box information and filter ad buys to the shows their target audience is
            most passionate about - and just as importantly avoid the shows they dislike.
          </div>
          <div class="bullet-header">Increase Reach</div>
          <div class="bullet-text">On social, brand managers are discovering the not-so-obvious interests correlated
            with their brand or product (TV program, album, film, celebrity, etc.) and broadening their targeting to
            include them, increasing targeted reach by 3-7x per campaign.
          </div>
          <div class="bullet-header">Make Data-Driven Creative Decisions</div>
          <div class="bullet-text">At the creative stage, marketers can use the deep interest data in Ranker Insights
            audience profiling to get a better handle on their target audience, and design their creative accordingly.
          </div>
          <div class="bullet-header">Identify Talent, Influencers and Advocates</div>
          <div class="bullet-text">Studios and new media brands are using Ranker Insights data to inform decisions on
            casting, influencers, and identifying brand ambassadors, even going so far as to filter audience affinity
            based on their intelligence, coolness factor, beauty, or talent.
          </div>
        </div>
        <div class="right-background"></div>
      </div>

      <div id="how">
        <div class="left-background"></div>
        <div class="section-content"></div>
        <div class="right-background"></div>
      </div>
    </main>
    <SiteFooter/>
  </div>
</template>

<script>
import SiteHeader from '@/components/layout/SiteHeader';
import SiteFooter from '@/components/layout/SiteFooter';
import NodeCycler from '@/components/NodeCycler';

export default {
  name: 'AboutPage',
  components: {
    NodeCycler,
    SiteHeader,
    SiteFooter
  },
  methods: {
    getInsightsUrl() {
      return process.env.INSIGHTS_V1_HOST;
    },
  }
};
</script>

<style lang="postcss">
.about {
  .splash-page-container {
    .vertical-center {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: flex-start;
      align-content: stretch;
      align-items: center;
    }

    span.link-blue {
      color: #086cb8;
    }

    .vertical-center > * {
      -webkit-box-flex: 0;
      -moz-box-flex: 0;
      -webkit-flex: 0 1 auto;
      -ms-flex: 0 1 auto;
      flex: 0 1 auto;
    }

    #info {
      width: 100%;
      min-width: 1200px;
    }

    .info-header-a {
      font-family: roboto bold, roboto-bold, Roboto, Arial, Helevtica, sans-serif;
      font-weight: 700;
      color: #022e63;
      font-size: 51px;
      text-align: center;
      /*text-shadow: 2px 3px 5px rgba(68, 68, 68, 0.38);*/
      width: 100%;
      white-space: nowrap;
    }

    p, .info-body-copy-a {
      font-family: 'Roboto Light', 'Roboto-Light', Roboto, Roboto-Regular, Arial, Helevtica, sans-serif;
      font-weight: 300;
      margin-bottom: 14px;
      color: #040404;
      font-size: 19px;
      line-height: 30px;
      text-align: justify;
      padding-left: 10px;
      padding-right: 10px;
      width: 100%;
    }

    .left-background,
    .right-background {
      order: 0;
      flex: 1 1 auto;
      align-self: stretch;
    }
  }


  #amplify-audience {
    @apply flex justify-center mt-10;
    height: 581px;
    background-color: #ffffff;

    .section-content {
      min-width: 1170px;
      width: calc(100vw - 30px);
      max-width: 1270px;

      .info-header-a {
        margin-top: 50px;
        margin-bottom: 23px;
        font-size: 47px;
      }

      .info-body-copy-a {
        position: relative;
        top: 4px;
      }

      .left-section {
        float: left;
        min-width: 800px;
        width: calc(100vw - 400px);
        max-width: 870px;

        #insert-paragraph {
          background-image: url(https://insights.ranker.com/images/splash/insights_insert_paragraph.png);
          background-repeat: no-repeat;
          width: 706px;
          height: 81px;
          margin-left: auto;
          margin-right: auto;
        }
      }

      .right-section {
        float: right;
        width: 370px;
      }
    }
  }

  #examples {
    @apply flex justify-center;
    background-color: #fcfcfc;

    .section-content {
      min-width: 1170px;
      width: calc(100vw - 30px);
      max-width: 1276px;
      background-color: #fcfcfc;
      background-image: url(https://insights.ranker.com/images/splash/insights_section_5_gradient.png);
      background-position: top;
      background-repeat: repeat-x;

      .info-header-a {
        margin-top: 44px;
        margin-bottom: 19px;
        font-size: 47px;
      }

      #node-examples {
        margin-left: auto;
        margin-right: auto;
        margin-bottom: 106px;
        height: 377px;
        min-width: 1170px;
        width: calc(100vw - 24px);
        max-width: 1276px;

        .node-example {
          min-width: 292.5px;
          width: calc(100vw / 4 - 24px / 4);
          max-width: 319px;
          height: 361px;
          float: left;
          background-position: center;
          background-size: contain;
          background-repeat: no-repeat;

          .node-example-button {
            background-position: center;
            background-size: contain;
            background-repeat: no-repeat;
            background-image: url(https://insights.ranker.com/images/splash/insights_section_5_button_static.png);
            width: 160px;
            height: 40px;
            margin-left: auto;
            margin-right: auto;
            position: relative;
            bottom: -393px;
          }

          &:hover .node-example-button {
            background-image: url(https://insights.ranker.com/images/splash/insights_section_5_button_hover.png);
          }
        }

        #node-example-1 {
          background-image: url(https://insights.ranker.com/images/splash/insights_section_5_example_1.png);
        }

        #node-example-2 {
          background-image: url(https://insights.ranker.com/images/splash/insights_section_5_example_2.png);
        }

        #node-example-3 {
          background-image: url(https://insights.ranker.com/images/splash/insights_section_5_example_3.png);
        }

        #node-example-4 {
          background-image: url(https://insights.ranker.com/images/splash/insights_section_5_example_4.png);
        }
      }

      #example-report-links {
        padding-left: 0px;

        .view-report-link {
          font-family: 'Roboto Bold', sans-serif;

          display: block;
          float: left;
          margin: 0 98px;
          font-size: 19px;

          color: #3894e6;
          text-decoration: none !important;

          &:hover {
            color: #2d72af;
          }

          &:nth-child(2) {
            margin-left: 104px;
            margin-right: 93px;
          }

          &:last-child {
            margin-left: 102px;
            margin-right: 0;
          }
        }
      }

      #example-bottom {
        display: none;
        margin-bottom: 80px;
      }
    }

    .left-background, .right-background {
      background-color: #fcfcfc;
      background-image: url(https://insights.ranker.com/images/splash/insights_section_5_gradient.png);
      background-position: top;
      background-repeat: repeat-x;
    }
  }

  #map {
    @apply flex justify-center;

    .section-content {
      z-index: 1;
      min-height: 533px;
      height: calc((100vw - 30px) / 2.193436960276339);
      max-height: 579px;
      min-width: 1170px;
      width: calc(100vw - 30px);
      max-width: 1270px;
      background-image: url(https://insights.ranker.com/images/splash/insights_section_4_image.jpg);
      background-color: #022e63;
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
    }

    .left-background, .right-background {
      background-color: #022e63;
    }
  }

  #bullets-header {
    @apply flex justify-center;
    background-color: #fcfcfc;

    .section-content {
      height: 148px;
      width: 1170px;

      .info-header-a {
        margin-top: 56px;
      }
    }
  }

  #bullets {
    @apply flex justify-center;

    .section-content {
      height: 580px;
      width: 1170px;
      background-image: url(https://insights.ranker.com/images/splash/insights_section_7_image.jpg);
      background-position: left;
      background-repeat: no-repeat;
      padding-left: 674px;

      .text-allow-overflow {
        white-space: nowrap;
      }

      .bullet-header {
        font-family: 'Roboto Condensed Bold', 'RobotoCondensed-Bold', 'Roboto Condensed', RobotoCondensed, Arial, Helevtica, sans-serif;
        font-weight: 700;
        color: #25242f;
        font-size: 25px;
        text-align: left;
        margin-top: 12px;

        &:first-child {
          margin-top: 21px;
        }

        width: 495px;
      }

      .bullet-text {
        font-family: 'Roboto Light', 'Roboto-Light', Roboto, Roboto-Regular, Arial, Helevtica, sans-serif;
        font-weight: 300;
        font-size: 15px;
        line-height: 24px;
        text-align: left;
        color: #25242f;
        width: 496px;
        max-height: 105px;
        padding-left: 0;
        padding-right: 0;
      }
    }

    .left-background, .right-background {
      background-color: #bdc9d6;
    }
  }

  #how {
    @apply flex justify-center;

    .section-content {
      height: 758px;
      min-width: 1170px;
      width: calc(100vw - 30px);
      max-width: 1267px;
      background-image: url(https://insights.ranker.com/images/splash/insights_how_image.jpg);
      background-repeat: no-repeat;
    }

    background-color: #eaeef2;
  }
}
</style>